import {ChangeDetectionStrategy, Component, computed, inject, signal} from "@angular/core"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {SliderComponent} from "@common/components/inputs/slider/slider.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {DropFilesComponent} from "@common/components/files/drop-files/drop-files.component"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {HintComponent} from "@common/components/hint/hint.component"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {ColorInputComponent} from "@common/components/inputs/color-input/color-input.component"
import {MimeType} from "@legacy/helpers/utils"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {OperatorRecolor} from "@app/textures/texture-editor/operator-stack/operators/recolor/operator-recolor"
import {OperatorPanelBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-panel-base"
import {computeTargetStatsFromImageFile} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/compute-target-stats-from-image-file"
import {computeLabMeanFromColor} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/compute-lab-mean-from-color"

export enum RecolorTargetStatsMode {
    ReferenceImage = "reference-image",
    ColorPicker = "color-picker",
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-recolor-panel",
    templateUrl: "./recolor-panel.component.html",
    styleUrls: ["./recolor-panel.component.scss"],
    imports: [
        InspectorSectionComponent,
        SliderComponent,
        InputContainerComponent,
        NumericInputComponent,
        DropFilesComponent,
        LoadingSpinnerIconComponent,
        HintComponent,
        ButtonComponent,
        ColorInputComponent,
    ],
})
export class RecolorPanelComponent extends OperatorPanelBase<OperatorRecolor> {
    protected readonly MimeType = MimeType
    protected readonly targetStatsModes = RecolorTargetStatsMode

    private readonly notifications = inject(NotificationsService)

    protected readonly $computingTargetStats = signal(false)
    protected readonly $targetStatsMode = signal(RecolorTargetStatsMode.ReferenceImage)
    protected readonly $isDebugEnabled = computed(() => this.$operator().callback.isDebugEnabled ?? false)

    protected setTargetStatsMode(mode: RecolorTargetStatsMode): void {
        this.$targetStatsMode.set(mode)
    }

    protected async onReferenceImageDropped(files: File[]): Promise<void> {
        const file = files[0]
        if (!file) return
        this.$computingTargetStats.set(true)
        try {
            const targetStats = await computeTargetStatsFromImageFile(file, this.$operator().callback.imageOpSystem, this.$operator().callback.texturesApi)
            this.$operator().setTargetStats(targetStats)
        } catch {
            this.notifications.showErrorString("Could not read the image file.")
        } finally {
            this.$computingTargetStats.set(false)
        }
    }

    protected async onColorPicked(rgb: [number, number, number]): Promise<void> {
        this.$computingTargetStats.set(true)
        try {
            const operator = this.$operator()
            const {l, a, b} = await computeLabMeanFromColor(rgb, operator.callback.imageOpSystem)
            // Only the mean shifts to the picked color; stddev is left at the source image's own
            // (or, failing that, whatever is currently set) so the texture's detail/contrast is
            // preserved instead of being flattened to a solid color.
            const sourceStats = operator.getSourceStatsForCurrentTextureType()
            operator.setTargetStats({
                l: {mean: l, stddev: sourceStats?.l.stddev ?? operator.$targetStddevL()},
                a: {mean: a, stddev: sourceStats?.a.stddev ?? operator.$targetStddevA()},
                b: {mean: b, stddev: sourceStats?.b.stddev ?? operator.$targetStddevB()},
            })
        } catch {
            this.notifications.showErrorString("Could not compute target color statistics.")
        } finally {
            this.$computingTargetStats.set(false)
        }
    }

    readonly $targetMeanL = computed(() => this.$operator().$targetMeanL())

    protected setTargetMeanL(value: number | null | undefined) {
        if (value != null) this.$operator().setTargetMeanL(value)
    }

    readonly $targetStddevL = computed(() => this.$operator().$targetStddevL())

    protected setTargetStddevL(value: number | null | undefined) {
        if (value != null) this.$operator().setTargetStddevL(value)
    }

    readonly $targetMeanA = computed(() => this.$operator().$targetMeanA())

    protected setTargetMeanA(value: number | null | undefined) {
        if (value != null) this.$operator().setTargetMeanA(value)
    }

    readonly $targetStddevA = computed(() => this.$operator().$targetStddevA())

    protected setTargetStddevA(value: number | null | undefined) {
        if (value != null) this.$operator().setTargetStddevA(value)
    }

    readonly $targetMeanB = computed(() => this.$operator().$targetMeanB())

    protected setTargetMeanB(value: number | null | undefined) {
        if (value != null) this.$operator().setTargetMeanB(value)
    }

    readonly $targetStddevB = computed(() => this.$operator().$targetStddevB())

    protected setTargetStddevB(value: number | null | undefined) {
        if (value != null) this.$operator().setTargetStddevB(value)
    }

    readonly $luminance = computed(() => this.$operator().$luminance())

    protected setLuminance(value: number) {
        this.$operator().setLuminance(value)
    }

    readonly $colorIntensity = computed(() => this.$operator().$colorIntensity())

    protected setColorIntensity(value: number) {
        this.$operator().setColorIntensity(value)
    }

    readonly $fade = computed(() => this.$operator().$fade())

    protected setFade(value: number) {
        this.$operator().setFade(value)
    }
}
